import { useEffect, useRef, useState } from "react"
import debounce from "lodash/debounce"
import throttle from "lodash/throttle"

function isInViewport(el) {
  const rect = el.getBoundingClientRect()
  const windowHeight =
    window.innerHeight || document.documentElement.clientHeight
  return rect.top < windowHeight && rect.bottom >= 0
}

export default function useScrollTracker(elementIds: string[]) {
  const currentRef = useRef(elementIds[0])
  const elementDataRef = useRef<
    {
      el: HTMLElement
      id: string
      height: number
      top: number
    }[]
  >([])
  const [currentSection, setCurrentSection] = useState(elementIds[0])

  useEffect(() => {
    const sectionElements = elementIds.map((id) => document.getElementById(id))
    let isMounted = true // to prevent throttle from running after unmount

    // Populate data array
    elementDataRef.current = []
    for (let el of sectionElements) {
      if (el) {
        elementDataRef.current.push({
          el,
          id: el.id,
          height: el.offsetHeight,
          top: el.getBoundingClientRect().top + window.pageYOffset,
        })
      }
    }

    // On scroll
    const setCurrent = throttle(() => {
      if (isMounted) {
        // Set current section
        for (let el of elementDataRef.current) {
          // If in view
          if (isInViewport(el.el)) {
            currentRef.current = el.id
            break
          }
        }
        setCurrentSection(currentRef.current)
      }
    }, 50)

    // Update values on resize
    const handleResize = debounce(() => {
      for (let el of elementDataRef.current) {
        el.height = el.el.offsetHeight
        el.top = el.el.getBoundingClientRect().top + window.pageYOffset
      }
      setCurrent()
    }, 400)

    document.addEventListener("scroll", setCurrent)
    window.addEventListener("resize", handleResize)
    return () => {
      isMounted = false
      document.removeEventListener("scroll", setCurrent)
      window.removeEventListener("resize", handleResize)
    }
  }, [elementIds])

  return currentSection
}
